// src/App.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Message, streamChat, uploadFile, UploadedFileResult } from './api';
import { API_URL, APP_NAME } from './config';
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfileModal from './components/ProfileModal';
import ChatHistory from './components/ChatHistory';
import MessageBubble, { ChatMessage } from './components/MessageBubble';
import PreviewPanel from './components/PreviewPanel';
import { PreviewFile } from './utils/extractFiles';
// Suppress missing type declarations for CSS side-effect import in TS
// @ts-ignore: Implicit any for CSS module
import './App.css';

const SUGGESTIONS = [
  'Debug my Python TypeError',
  'Explain async/await in JavaScript',
  'Write a REST API in Express.js',
  'Fix my CSS flexbox layout',
  'Convert this function to TypeScript',
  'Write unit tests for my code',
];

const ACCEPTED_FILE_TYPES = '.pdf,.docx,.png,.jpg,.jpeg';

function generateId() { return Math.random().toString(36).slice(2); }

export default function App() {
  // ─── Auth ──────────────────────────────────────────────────────────────────
  const { user, loading: authLoading, login, register, logout, updateAvatar, getToken } =
    useAuth();
  const [authPage, setAuthPage] = useState<'login' | 'register'>('login');

  // ─── Chat state ────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [showProfile, setShowProfile] = useState(false);
  const [preview, setPreview] = useState<PreviewFile[] | null>(null);

  // ─── Sidebar toggle ────────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ─── Edit message state ────────────────────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  // ─── Phase 1: attached file state ──────────────────────────────────────────
  const [attachedFile, setAttachedFile] = useState<UploadedFileResult | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ─── Throttled token flushing ──────────────────────────────────────────────
  // Buffers tokens per-message and commits to state at most once per animation
  // frame instead of on every single token. This is the main fix for streaming
  // feeling slow/janky on long responses (was: full markdown re-parse per token).
  const flushMapRef = useRef<Record<string, { buffer: string; scheduled: boolean }>>({});

  const scheduleFlush = useCallback((id: string) => {
    const entry = flushMapRef.current[id];
    if (!entry || entry.scheduled) return;
    entry.scheduled = true;
    requestAnimationFrame(() => {
      const e = flushMapRef.current[id];
      if (!e) return;
      const chunk = e.buffer;
      e.buffer = '';
      e.scheduled = false;
      if (chunk) {
        setMessages(prev => prev.map(m => m.id === id ? { ...m, content: m.content + chunk } : m));
      }
    });
  }, []);

  const bufferToken = useCallback((id: string, token: string) => {
    const entry = flushMapRef.current[id] || (flushMapRef.current[id] = { buffer: '', scheduled: false });
    entry.buffer += token;
    scheduleFlush(id);
  }, [scheduleFlush]);

  const finalizeMessage = useCallback((id: string) => {
    const entry = flushMapRef.current[id];
    if (entry && entry.buffer) {
      const chunk = entry.buffer;
      setMessages(prev => prev.map(m => m.id === id ? { ...m, content: m.content + chunk, streaming: false } : m));
    } else {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, streaming: false } : m));
    }
    delete flushMapRef.current[id];
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Load a saved chat ────────────────────────────────────────────────────
  const loadChat = useCallback(async (id: number) => {
    setActiveChatId(id);
    setError('');
    const r = await fetch(`${API_URL}/chats.php?id=${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    const data = await r.json();
    if (!r.ok) return;
    const msgs: ChatMessage[] = (data.chat.messages || []).map((m: any) => ({
      id: generateId(), role: m.role, content: m.content
    }));
    setMessages(msgs);
  }, [getToken]);

  // ─── Save messages to backend (parallelized — was sequential awaits) ──────
  const saveMessage = useCallback(async (chatId: number, role: string, content: string) => {
    await fetch(`${API_URL}/chats.php?id=${chatId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ role, content })
    });
  }, [getToken]);

  const persistExchange = useCallback(async (userContent: string, assistantContent: string) => {
    if (!user) return;
    let cid = activeChatId;
    if (!cid) {
      const title = userContent.slice(0, 60);
      const r = await fetch(`${API_URL}/chats.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ title, messages: [] })
      });
      const d = await r.json();
      cid = d.chat_id;
      setActiveChatId(cid);
      setHistoryRefresh(n => n + 1);
    }
    if (cid) {
      await Promise.all([
        saveMessage(cid, 'user', userContent),
        saveMessage(cid, 'assistant', assistantContent)
      ]);
    }
  }, [user, activeChatId, getToken, saveMessage]);

  // Regenerate saves ONLY the fresh assistant reply — reusing
  // persistExchange here would re-insert a duplicate copy of the user's
  // original message every time you regenerate. Regeneration only ever
  // happens on an already-persisted chat (there has to be a prior
  // exchange to regenerate), so activeChatId is expected to exist; if it
  // somehow doesn't, this silently does nothing rather than creating a
  // new chat with an orphaned assistant-only message.
  const persistAssistantOnly = useCallback(async (assistantContent: string) => {
    if (!user || !activeChatId) return;
    await saveMessage(activeChatId, 'assistant', assistantContent);
  }, [user, activeChatId, saveMessage]);

  // ─── Shared streaming runner (used by send, edit, and regenerate) ────────
  const runStream = useCallback(async (
    history: Message[],
    userContent: string,
    persistMode: 'both' | 'assistant-only' = 'both'
  ) => {
    const assistantId = generateId();
    const assistantMsg: ChatMessage = { id: assistantId, role: 'assistant', content: '', streaming: true };
    setMessages(prev => [...prev, assistantMsg]);
    setChatLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    let fullResponse = '';
    try {
      for await (const _ of streamChat(history, (token) => {
        fullResponse += token;
        bufferToken(assistantId, token);
      }, controller.signal)) {}

      finalizeMessage(assistantId);
      if (persistMode === 'assistant-only') {
        await persistAssistantOnly(fullResponse);
      } else {
        await persistExchange(userContent, fullResponse);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(
        err.message.includes('fetch') || err.message.includes('Failed')
          ? '⚠ Cannot reach AI server. Please try again in a moment.'
          : `Error: ${err.message}`
      );
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setChatLoading(false);
      abortRef.current = null;
    }
  }, [bufferToken, finalizeMessage, persistExchange, persistAssistantOnly]);

  // ─── Phase 1: attach / remove a file ───────────────────────────────────────
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input value immediately so selecting the same file twice
    // in a row still fires onChange.
    e.target.value = '';
    if (!file) return;

    setUploadError('');
    setUploadingFile(true);
    try {
      const result = await uploadFile(file, getToken());
      setAttachedFile(result);
    } catch (err: any) {
      setUploadError(err.message || 'Failed to read that file.');
    } finally {
      setUploadingFile(false);
    }
  }, [getToken]);

  const removeAttachedFile = useCallback(() => {
    setAttachedFile(null);
    setUploadError('');
  }, []);

  // ─── Send message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text?: string) => {
    const typed = (text || input).trim();
    if ((!typed && !attachedFile) || chatLoading) return;
    setInput('');
    setError('');

    // What the user actually sees in the chat bubble.
    const displayContent = attachedFile
      ? `${typed}${typed ? '\n\n' : ''}📎 ${attachedFile.filename}`
      : typed;

    const userMsg: ChatMessage = { id: generateId(), role: 'user', content: displayContent };
    setMessages(prev => [...prev, userMsg]);

    // Hidden context message with the extracted file content — sent to the
    // model but never rendered in the UI and never saved to chat history.
    // This keeps the DB and the visible transcript clean, and slots into
    // the existing history/userContent split that runStream already has.
    const fileContext: Message[] = attachedFile
      ? [{
          role: 'user',
          content: `The user attached a file named "${attachedFile.filename}". `
            + `Extracted content follows:\n\n${attachedFile.content}`
        }]
      : [];

    const effectiveTyped = typed || 'Please analyze the attached file.';

    const history: Message[] = [
      ...messages.map(m => ({ role: m.role, content: m.content })),
      ...fileContext,
      { role: 'user', content: effectiveTyped }
    ];

    setAttachedFile(null);
    await runStream(history, displayContent);
  }, [input, chatLoading, messages, runStream, attachedFile]);

  // ─── Edit message handlers ─────────────────────────────────────────────────
  const startEdit = (msg: ChatMessage) => {
    setEditingId(msg.id);
    setEditText(msg.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const submitEdit = useCallback(async (msgId: string) => {
    const content = editText.trim();
    if (!content || chatLoading) return;

    const idx = messages.findIndex(m => m.id === msgId);
    if (idx === -1) return;

    const before = messages.slice(0, idx);
    setEditingId(null);
    setEditText('');
    setError('');

    const updatedUserMsg: ChatMessage = { id: generateId(), role: 'user', content };
    setMessages([...before, updatedUserMsg]);

    const history: Message[] = [
      ...before.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content }
    ];

    await runStream(history, content);
  }, [editText, chatLoading, messages, runStream]);

  // ─── Regenerate: drop the last assistant reply, re-ask with the same
  // history, save only the new assistant message (never a duplicate user
  // message) — see persistAssistantOnly above for why.
  const regenerateResponse = useCallback(async (assistantMsgId: string) => {
    if (chatLoading) return;

    const idx = messages.findIndex(m => m.id === assistantMsgId);
    if (idx === -1) return;

    const before = messages.slice(0, idx); // everything up to and including the preceding user message
    const lastUserMsg = [...before].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return; // nothing to regenerate from

    setMessages(before);
    setError('');

    const history: Message[] = before.map(m => ({ role: m.role, content: m.content }));
    await runStream(history, lastUserMsg.content, 'assistant-only');
  }, [chatLoading, messages, runStream]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const stopGeneration = () => abortRef.current?.abort();

  const startNewChat = () => {
    setMessages([]);
    setError('');
    setActiveChatId(null);
    setAttachedFile(null);
    setUploadError('');
  };

  const openPreview = useCallback((files: PreviewFile[]) => {
    setPreview(files);
  }, []);

  // ─── Loading splash ────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-loading">⚡ Loading…</div>
      </div>
    );
  }

  // ─── Auth pages ────────────────────────────────────────────────────────────
  if (!user) {
    return authPage === 'login'
      ? <LoginPage onLogin={login} onGoRegister={() => setAuthPage('register')} />
      : <RegisterPage onRegister={register} onGoLogin={() => setAuthPage('login')} />;
  }

  // ─── Main app ──────────────────────────────────────────────────────────────
  return (
    <div className={`app ${preview ? 'preview-open' : ''}`}>
      {showProfile && (
        <ProfileModal
          user={user}
          token={getToken()}
          onAvatarUpdate={updateAvatar}
          onClose={() => setShowProfile(false)}
          onLogout={() => { logout(); setShowProfile(false); }}
        />
      )}

      <button
        className={`sidebar-toggle ${sidebarOpen ? 'open' : 'closed'}`}
        onClick={() => setSidebarOpen(o => !o)}
        title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>

      <aside className={`sidebar ${sidebarOpen ? '' : 'sidebar-hidden'}`}>
        <div className="sidebar-header">
          <div className="logo">
            <svg className="logo-svg" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="36" height="36" rx="10" fill="#a78bfa"/>
              <path d="M10 13h4l2 3-2 3h-4l-2-3 2-3z" fill="#0f0f17"/>
              <path d="M18 9l2 2-2 2-2-2 2-2z" fill="#0f0f17" opacity="0.6"/>
              <rect x="14" y="17" width="12" height="2" rx="1" fill="#0f0f17"/>
              <rect x="14" y="21" width="8" height="2" rx="1" fill="#0f0f17" opacity="0.7"/>
              <circle cx="26" cy="24" r="4" fill="#60a5fa"/>
              <path d="M24.5 24l1 1 2-2" stroke="#0f0f17" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="logo-text">{APP_NAME}</span>
          </div>
        </div>

        <button className="new-chat-btn" onClick={startNewChat}>+ New Chat</button>

        <div className="sidebar-section">
          <p className="sidebar-label">Chat History</p>
          <ChatHistory
            token={getToken()}
            activeChatId={activeChatId}
            onSelectChat={loadChat}
            onNewChat={startNewChat}
            refreshTrigger={historyRefresh}
          />
        </div>

        <div className="sidebar-footer user-footer" onClick={() => setShowProfile(true)}>
          <div className="user-avatar-sm">
            {user.avatar_url
              ? <img src={user.avatar_url} alt="avatar" />
              : <span>{user.name.charAt(0).toUpperCase()}</span>
            }
          </div>
          <div className="user-info-sm">
            <p className="user-name-sm">{user.name}</p>
            <p className="user-email-sm">{user.email}</p>
          </div>
          <span className="edit-icon">✏️</span>
        </div>
      </aside>

      <main className="main">
        <div className="chat-container">
          {messages.length === 0 ? (
            <div className="welcome">
              <div className="welcome-icon">⚡</div>
              <h1>Hello, {user.name.split(' ')[0]}!</h1>
              <p>Your free AI assistant for coding and text tasks.</p>
              <div className="welcome-chips">
                {SUGGESTIONS.slice(0, 3).map(s => (
                  <button key={s} className="chip" onClick={() => sendMessage(s)}>{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                isUser={msg.role === 'user'}
                avatarUrl={msg.role === 'user' ? user.avatar_url : undefined}
                userInitial={user.name.charAt(0).toUpperCase()}
                isEditing={editingId === msg.id}
                editText={editText}
                onEditTextChange={setEditText}
                onStartEdit={() => startEdit(msg)}
                onSubmitEdit={() => submitEdit(msg.id)}
                onCancelEdit={cancelEdit}
                canEdit={!chatLoading && !msg.streaming}
                onPreview={openPreview}
                isLastMessage={idx === messages.length - 1}
                onRegenerate={() => regenerateResponse(msg.id)}
                canRegenerate={!chatLoading}
              />
            ))
          )}
          {error && <div className="error-banner">{error}</div>}
          <div ref={bottomRef} />
        </div>

        <div className="input-area">
          {(attachedFile || uploadingFile || uploadError) && (
            <div className="attach-chip-row">
              {uploadingFile && (
                <div className="attach-chip attach-chip-loading">
                  <span className="gen-spinner" /> Reading file…
                </div>
              )}
              {attachedFile && !uploadingFile && (
                <div className="attach-chip">
                  📎 {attachedFile.filename}
                  <button
                    className="attach-chip-remove"
                    onClick={removeAttachedFile}
                    title="Remove attached file"
                    type="button"
                  >
                    ✕
                  </button>
                </div>
              )}
              {uploadError && (
                <div className="attach-chip attach-chip-error">{uploadError}</div>
              )}
            </div>
          )}

          <div className="input-box">
            <input
              type="file"
              ref={fileInputRef}
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={chatLoading || uploadingFile}
              title="Attach a PDF, DOCX, PNG, or JPG"
              type="button"
            >
              📎
            </button>

            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={attachedFile ? 'Ask something about this file…' : 'Ask anything about code or text'}
              rows={1}
              disabled={chatLoading}
            />
            {chatLoading ? (
              <div className="generating-inline">
                <span className="gen-spinner" />
                <span className="generating-label">Generating…</span>
                <button className="send-btn stop" onClick={stopGeneration}>⏹ Stop</button>
              </div>
            ) : (
              <button
                className="send-btn"
                onClick={() => sendMessage()}
                disabled={!input.trim() && !attachedFile}
              >➤</button>
            )}
          </div>
          <p className="disclaimer">Free AI — Powered by Professor Raimal Raja.</p>
        </div>
      </main>

      {preview && (
        <PreviewPanel
          files={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}